// Editable vector paths: subpaths of nodes `{ x, y, i, o }`, `i`/`o` absolute handle points or null on a straight side.
import { stringToRawPath } from '~/lib/svg/paths'

const EPS = 1e-3
const KAPPA = 0.5522847498
const near = (a, b) => Math.abs(a[0] - b[0]) < EPS && Math.abs(a[1] - b[1]) < EPS
const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
const pt = n => [n.x, n.y]
const r2 = v => Math.round(v * 100) / 100

export const parsePath = d => {
	let raw
	try {
		raw = stringToRawPath(d || '')
	} catch {
		return []
	}
	return raw
		.map(seg => {
			const n = (seg.length - 2) / 6
			const pts = []
			for (let k = 0; k <= n; k++) pts.push([seg[6 * k], seg[6 * k + 1]])
			const closed = !!seg.closed
			const count = closed && n > 0 && near(pts[0], pts[n]) ? n : n + 1
			const nodes = pts.slice(0, count).map(([x, y]) => ({ x, y, i: null, o: null }))
			for (let k = 0; k < n; k++) {
				const a = pts[k]
				const b = pts[k + 1]
				const c1 = [seg[6 * k + 2], seg[6 * k + 3]]
				const c2 = [seg[6 * k + 4], seg[6 * k + 5]]
				if (near(c1, lerp(a, b, 1 / 3)) && near(c2, lerp(a, b, 2 / 3))) continue
				if (!near(c1, a)) nodes[k].o = c1
				if (!near(c2, b)) nodes[(k + 1) % count].i = c2
			}
			return { closed, nodes }
		})
		.filter(sp => sp.nodes.length)
}

const segment = (a, b) => {
	if (!a.o && !b.i) return `L${r2(b.x)} ${r2(b.y)}`
	const c1 = a.o ?? pt(a)
	const c2 = b.i ?? pt(b)
	return `C${r2(c1[0])} ${r2(c1[1])} ${r2(c2[0])} ${r2(c2[1])} ${r2(b.x)} ${r2(b.y)}`
}

export const pathString = subpaths =>
	subpaths
		.filter(sp => sp.nodes.length)
		.map(({ closed, nodes }) => {
			let d = `M${r2(nodes[0].x)} ${r2(nodes[0].y)}`
			for (let k = 1; k < nodes.length; k++) d += segment(nodes[k - 1], nodes[k])
			if (closed) {
				const last = nodes[nodes.length - 1]
				if (last.o || nodes[0].i) d += segment(last, nodes[0])
				d += 'Z'
			}
			return d
		})
		.join('')

export const mapNodes = (subpaths, fn) =>
	subpaths.map(sp => ({
		...sp,
		nodes: sp.nodes.map(n => {
			const [x, y] = fn(pt(n))
			return { x, y, i: n.i && fn(n.i), o: n.o && fn(n.o) }
		}),
	}))

export const rectNodes = (x, y, w, h) => [
	{
		closed: true,
		nodes: [
			[x, y],
			[x + w, y],
			[x + w, y + h],
			[x, y + h],
		].map(([px, py]) => ({ x: px, y: py, i: null, o: null })),
	},
]

export const ellipseNodes = (cx, cy, rx, ry) => {
	const kx = rx * KAPPA
	const ky = ry * KAPPA
	return [
		{
			closed: true,
			nodes: [
				{ x: cx, y: cy - ry, i: [cx - kx, cy - ry], o: [cx + kx, cy - ry] },
				{ x: cx + rx, y: cy, i: [cx + rx, cy - ky], o: [cx + rx, cy + ky] },
				{ x: cx, y: cy + ry, i: [cx + kx, cy + ry], o: [cx - kx, cy + ry] },
				{ x: cx - rx, y: cy, i: [cx - rx, cy + ky], o: [cx - rx, cy - ky] },
			],
		},
	]
}
